/**
 * 한글 텍스트를 빨간 원형 도장 PNG로 변환
 * - 원형 / 사각형 / 타원형 형태 지원
 * - 투명 배경, 빨간색 도장 인쇄 느낌
 */

export type SealShape = "circle" | "square" | "ellipse";

export interface GenerateSealOptions {
  text: string; // 한글 (최대 6자 권장)
  shape?: SealShape;
  size?: number; // px (정사각 캔버스)
  color?: string; // 도장 색
  /** 살짝 빛바랜 인쇄 느낌 (잉크 농도 75%) */
  inkBleed?: boolean;
}

export const generateSealDataUrl = (opts: GenerateSealOptions): string => {
  const {
    text,
    shape = "circle",
    size = 300,
    color = "#C8102E", // 인주 빨강
    inkBleed = true,
  } = opts;

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas context not available");

  // 투명 초기화
  ctx.clearRect(0, 0, size, size);

  // 외곽선
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(6, size * 0.04);
  ctx.fillStyle = color;

  const cx = size / 2;
  const cy = size / 2;
  const pad = ctx.lineWidth;

  if (shape === "circle") {
    ctx.beginPath();
    ctx.arc(cx, cy, size / 2 - pad, 0, Math.PI * 2);
    ctx.stroke();
  } else if (shape === "ellipse") {
    ctx.beginPath();
    ctx.ellipse(cx, cy, size / 2 - pad, size / 3 - pad, 0, 0, Math.PI * 2);
    ctx.stroke();
  } else {
    ctx.strokeRect(pad, pad, size - pad * 2, size - pad * 2);
  }

  // 텍스트 — 글자 수에 따라 폰트 크기 자동 조정
  const chars = Array.from(text.trim()); // 한글 1자 단위
  const len = chars.length;
  const innerR = size / 2 - pad * 2;

  // 1~2글자 → 한 줄, 3~4글자 → 2x2 격자, 5~6글자 → 2x3 (또는 회전)
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `bold ${Math.floor(size * (len <= 2 ? 0.42 : len <= 4 ? 0.32 : 0.26))}px "맑은 고딕", "Malgun Gothic", "Apple SD Gothic Neo", "Noto Sans KR", sans-serif`;

  if (len <= 2) {
    ctx.fillText(text, cx, cy);
  } else if (len <= 4) {
    const offset = innerR * 0.32;
    ctx.fillText(chars[0], cx - offset, cy - offset);
    if (chars[1]) ctx.fillText(chars[1], cx + offset, cy - offset);
    if (chars[2]) ctx.fillText(chars[2], cx - offset, cy + offset);
    if (chars[3]) ctx.fillText(chars[3], cx + offset, cy + offset);
  } else {
    // 5~6글자: 2열 3행 또는 3열 2행
    const cols = len <= 6 ? 2 : 3;
    const rows = Math.ceil(len / cols);
    const dx = innerR * 0.5;
    const dy = innerR * 0.45;
    for (let i = 0; i < len; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = cx + (col - (cols - 1) / 2) * dx;
      const y = cy + (row - (rows - 1) / 2) * dy;
      ctx.fillText(chars[i], x, y);
    }
  }

  // 잉크 번짐 효과 — 살짝 투명 + 노이즈
  if (inkBleed) {
    const imageData = ctx.getImageData(0, 0, size, size);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] > 0) {
        // 약간의 alpha 변동 (75~95%)
        const noise = 0.75 + Math.random() * 0.2;
        data[i + 3] = Math.floor(data[i + 3] * noise);
      }
    }
    ctx.putImageData(imageData, 0, 0);
  }

  return canvas.toDataURL("image/png");
};

/** 손글씨 캔버스 데이터를 빨간 도장톤으로 변환 (검정 → 빨강, 알파 보존) */
export const tintCanvasToSealColor = (
  srcDataUrl: string,
  color = "#C8102E"
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject("ctx fail");
      ctx.drawImage(img, 0, 0);
      // 색 입히기
      ctx.globalCompositeOperation = "source-in";
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = srcDataUrl;
  });
};
